<template>
  <aside class="table-of-contents">
    <h3 class="toc-title">Sommaire</h3>
    <ul class="toc-list">
      <li v-for="heading in groupedHeadings" 
          :key="heading.id" 
          :class="['toc-item', { 'has-subitems': heading.children.length > 0 }]"
          :data-target="heading.id">
        <a :href="`#${heading.id}`" class="toc-link" @click.prevent="scrollToHeading(heading.id)">
          {{ heading.text }}
        </a>
        
        <button v-if="heading.children.length > 0" 
                class="toc-toggle" 
                @click="toggleSubItems(heading.id)">
          <i class="fas fa-chevron-right"></i>
        </button>
        
        <ul v-if="heading.children.length > 0" class="toc-subitems">
          <li v-for="child in heading.children" 
              :key="child.id" 
              class="toc-subitem"
              :data-target="child.id">
            <a :href="`#${child.id}`" class="toc-link" @click.prevent="scrollToHeading(child.id)">
              {{ child.text }}
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </aside>
</template>

<script>
export default {
  name: 'TableOfContents',
  props: {
    headings: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    groupedHeadings() {
      const h2Headings = this.headings.filter(h => h.tag === 'H2')
      
      return h2Headings.map(h2 => {
        const children = this.headings.filter(h => 
          h.tag === 'H3' && h.parentId === h2.id
        )
        return {
          ...h2,
          children
        }
      })
    }
  },
  methods: {
    scrollToHeading(id) {
      const element = document.getElementById(id)
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 120,
          behavior: 'smooth'
        })
      }
    },
    toggleSubItems(headingId) {
      const item = document.querySelector(`.toc-item[data-target="${headingId}"]`)
      if (item) {
        item.classList.toggle('expanded')
      }
    },
    setupIntersectionObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const targetId = entry.target.id
            const tocItems = document.querySelectorAll('.toc-item, .toc-subitem')
            
            tocItems.forEach(item => {
              item.classList.remove('active')
            })
            
            const tocItem = document.querySelector(`.toc-item[data-target="${targetId}"]`)
            const tocSubItem = document.querySelector(`.toc-subitem[data-target="${targetId}"]`)
            
            if (tocItem) {
              tocItem.classList.add('active')
              if (tocItem.classList.contains('has-subitems')) {
                tocItem.classList.add('expanded')
              }
            } else if (tocSubItem) {
              tocSubItem.classList.add('active')
              const parentItem = tocSubItem.closest('.toc-item')
              if (parentItem) {
                parentItem.classList.add('expanded')
                parentItem.classList.add('active')
              }
            }
          }
        })
      }, {
        rootMargin: '-100px 0px -50% 0px',
        threshold: 0.1
      })
      
      this.headings.forEach(heading => {
        const element = document.getElementById(heading.id)
        if (element) {
          observer.observe(element)
        }
      })
    }
  },
  mounted() {
    this.setupIntersectionObserver()
  }
}
</script>

<style scoped>
.table-of-contents {
  position: sticky;
  top: 100px;
  flex: 0 0 300px;
  height: fit-content;
  background: white;
  border-radius: var(--radius);
  padding: 25px;
  box-shadow: var(--shadow);
  max-height: calc(100vh - 150px);
  overflow-y: auto;
}

.toc-title {
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: var(--dark);
  padding-bottom: 10px;
  border-bottom: 2px solid var(--gray-light);
}

.toc-list {
  list-style: none;
}

.toc-item {
  margin-bottom: 12px;
  position: relative;
}

.toc-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 6px;
  height: 6px;
  background: var(--primary);
  border-radius: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: var(--transition);
}

.toc-item.active::before {
  opacity: 1;
}

.toc-link {
  color: var(--gray);
  text-decoration: none;
  padding-left: 15px;
  display: block;
  transition: var(--transition);
  font-size: 0.95rem;
  line-height: 1.5;
  cursor: pointer;
}

.toc-link:hover {
  color: var(--primary);
  padding-left: 20px;
}

.toc-item.active .toc-link {
  color: var(--primary);
  font-weight: 600;
}

.toc-subitems {
  list-style: none;
  margin-left: 15px;
  margin-top: 8px;
  display: none;
}

.toc-item.expanded .toc-subitems {
  display: block;
}

.toc-toggle {
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  color: var(--gray);
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
}

.toc-item.expanded .toc-toggle i {
  transform: rotate(90deg);
}

@media (max-width: 992px) {
  .table-of-contents {
    position: static;
    flex: 0 0 auto;
    max-height: none;
    margin-bottom: 30px;
  }
}
</style>